export type NotifActionKey =
  | "reconnect"
  | "open_order"
  | "open_inventory"
  | "open_inventory_mkp"
  | "open_diff"
  | "open_inbound";

type Marketplace = "shopee" | "lazada" | "tiktok";

/** Typed `navigate()` options — the same shapes `features/notifications`'s
 * `CtaNavigation` uses, redeclared here because rule #2 forbids importing
 * across features. ponytail: keep the two lists in step by hand. */
export type NotifNavigation =
  | { to: "/inventory-diff"; search: { highlight: string } }
  | { to: "/inbound-report"; search: { highlight: string } }
  | { to: "/inventory"; search: { search: string; offset: number; marketplace?: Marketplace } }
  | { to: "/orders/$saleOrderId"; params: { saleOrderId: string } }
  | { to: "/settings/marketplace-connections" };

export interface NotifAction {
  key: NotifActionKey;
  nav: NotifNavigation;
  /** Marketplace the action is scoped to, for the button label. */
  marketplace?: Marketplace;
}

interface NotifLike {
  type: string;
  entity?: string;
  target?: { view: string; sku: string; order_id?: string } | null;
}

function asMarketplace(v: string | undefined): Marketplace | undefined {
  return v === "shopee" || v === "lazada" || v === "tiktok" ? v : undefined;
}

/** The one thing ops can do about this alert, or null when there is nothing
 * to do but read it — such alerts stay in the bell and off the dashboard. */
export function actionFor(row: NotifLike): NotifAction | null {
  if (row.type === "marketplace_auth") {
    return { key: "reconnect", nav: { to: "/settings/marketplace-connections" } };
  }
  const target = row.target;
  if (!target) return null;
  switch (target.view) {
    case "diff":
      return {
        key: "open_diff",
        nav: { to: "/inventory-diff", search: { highlight: target.sku } },
      };
    case "inbound":
      return {
        key: "open_inbound",
        nav: { to: "/inbound-report", search: { highlight: target.sku } },
      };
    case "inventory":
      // Push-stock events carry the marketplace in `entity`, not a SKU —
      // land on that marketplace's inventory rather than the whole list.
      {
        const mkp = asMarketplace(row.entity);
        if (mkp !== undefined) {
          return {
            key: "open_inventory_mkp",
            marketplace: mkp,
            nav: { to: "/inventory", search: { search: target.sku, offset: 0, marketplace: mkp } },
          };
        }
      }
      return {
        key: "open_inventory",
        nav: { to: "/inventory", search: { search: target.sku, offset: 0 } },
      };
    case "order":
      return {
        key: "open_order",
        nav: { to: "/orders/$saleOrderId", params: { saleOrderId: target.order_id || target.sku } },
      };
    case "settings/marketplace-connections":
      return { key: "reconnect", nav: { to: "/settings/marketplace-connections" } };
    default:
      return null;
  }
}
